// src/components/AdminPage.tsx - PREMIUM REFACTOR
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash, 
  Copy,
  Loader2,
  Users,
  Shield,
  Database,
  Server,
  ChevronLeft,
  ChevronRight,
  X,
  RotateCcw,
  Activity,
  FileText
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useApi } from '../hooks/useApi';
import { API_BASE_URL } from '../constants';
import { useAuth } from '../hooks/useAuth';
import EditUserModal from './EditUserModal';
import SystemResetModal from './SystemResetModal';
import JobsTable from './JobsTable';
import LogsViewer from './LogsViewer';
import './AdminPage.css';

import type { User, UsersResponse } from '../types';

interface CreateUserForm {
  name: string;
  email: string;
  permissions: string[];
}

interface SystemStats {
  service?: string;
  version?: string;
  status?: string;
  total_users?: number;
  contacts_index_health?: {
    exists: boolean;
    status: string;
    document_count: number;
    size_mb: number;
  };
}



const AVAILABLE_PERMISSIONS = [
  'search', 'metadata', 'health', 'export', 'contacts', 'suggestions', 'billing', 'admin'
];

const AdminPage: React.FC = () => {
  const { setView, showToast, showConfirmationDialog, hideConfirmationDialog } = useAppContext();
  const { createUser, listUsers, deleteUserAdmin } = useApi();
  const { apiKey } = useAuth();
  
  const [users, setUsers] = useState<User[]>([]);
  const [systemStats, setSystemStats] = useState<SystemStats>({});
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [createForm, setCreateForm] = useState<CreateUserForm>({ name: '', email: '', permissions: ['billing'] });
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'users' | 'jobs' | 'logs'>('users');

  const pageSize = 25;

  const loadSystemStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/`, {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) setSystemStats(await response.json());
    } catch {
      setSystemStats({});
    } finally {
      setStatsLoading(false);
    }
  }, [apiKey]);

  const loadUsers = useCallback(async (currentPage: number = page) => {
    setLoading(true);
    try {
      const response = await listUsers(currentPage, pageSize) as UsersResponse;
      if (response?.users) {
        setUsers(response.users);
        setTotalPages(response.total_pages);
      } else {
        setUsers([]);
        setTotalPages(1);
      }
    } catch {
      showToast({ type: 'error', title: 'Error Loading Users', message: 'Failed to load users list' });
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [listUsers, showToast, page, pageSize]);

  useEffect(() => {
    loadUsers();
    loadSystemStats();
  }, [loadUsers, loadSystemStats]);

  const handleCreateUser = useCallback(async () => {
    if (!createForm.name.trim()) {
      showToast({ type: 'warning', title: 'Validation Error', message: 'Please enter a user name' });
      return;
    }
    if (createForm.permissions.length === 0) {
      showToast({ type: 'warning', title: 'Validation Error', message: 'Please select at least one permission' });
      return;
    }
    setSubmitting(true);
    try {
      const response = await createUser(
        createForm.name.trim(), 
        createForm.permissions, 
        createForm.email.trim() || undefined
      ) as User;
      if (response) {
        showToast({ type: 'success', title: 'User Created', message: `User "${response.name}" created.`, duration: 8000 });
        setCreateForm({ name: '', email: '', permissions: ['billing'] });
        setShowCreateModal(false);
        loadUsers();
      }
    } catch (error) {
      showToast({ type: 'error', title: 'Creation Failed', message: error instanceof Error ? error.message : 'Failed to create user' });
    } finally {
      setSubmitting(false);
    }
  }, [createForm, createUser, showToast, loadUsers]);

  const handleDeleteUser = useCallback((user: User) => {
    showConfirmationDialog({
      title: 'Delete User',
      message: `Are you sure you want to delete "${user.name}"? This action cannot be undone.`,
      confirmText: 'Delete User',
      cancelText: 'Cancel',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await deleteUserAdmin(user.api_key);
          showToast({ type: 'success', title: 'User Deleted', message: `User "${user.name}" has been deleted.` });
          loadUsers();
        } catch (error) {
          showToast({ type: 'error', title: 'Delete Failed', message: error instanceof Error ? error.message : 'Failed to delete user.' });
        }
        hideConfirmationDialog();
      },
      onCancel: hideConfirmationDialog
    });
  }, [deleteUserAdmin, showToast, loadUsers, showConfirmationDialog, hideConfirmationDialog]);

  const copyApiKey = useCallback(async (key: string, name: string) => {
    try {
      await navigator.clipboard.writeText(key);
      showToast({ type: 'success', title: 'API Key Copied', message: `API key for ${name} copied.` });
    } catch {
      showToast({ type: 'error', title: 'Copy Failed', message: 'Could not copy API key.' });
    }
  }, [showToast]);

  const toggleCreatePermission = useCallback((p: string) => {
    setCreateForm(prev => ({ ...prev, permissions: prev.permissions.includes(p) ? prev.permissions.filter(perm => perm !== p) : [...prev.permissions, p] }));
  }, []);

  const openEditModal = useCallback((user: User) => {
    setEditingUser(user);
    setShowEditModal(true);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    loadUsers(newPage);
  }, [loadUsers]);

  const statsCards = useMemo(() => [
    { icon: Users, label: 'Total Users', value: systemStats.total_users?.toString() ?? '...', color: '#3b82f6' },
    { icon: Shield, label: 'Active Users', value: users.filter(u => u.is_active).length.toString(), color: '#10b981' },
    { icon: Database, label: 'Contacts Health', value: systemStats.contacts_index_health?.status === 'healthy' ? 'Healthy' : 'Check', subtitle: systemStats.contacts_index_health?.document_count ? `${systemStats.contacts_index_health.document_count.toLocaleString()} docs` : undefined, color: '#8b5cf6' },
    { icon: Server, label: 'System Status', value: systemStats.status === '✅ OPERATIONAL' ? 'Online' : 'Check', subtitle: systemStats.version ? `v${systemStats.version}` : undefined, color: '#f59e0b' }
  ], [systemStats, users]);

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  const maskApiKey = (key: string) => key.length <= 8 ? key : `${key.slice(0, 4)}...${key.slice(-4)}`;

  if (loading && page === 1) {
    return <div className="section loading-section"><Loader2 size={32} className="loading-spinner" /><p>Loading Admin Panel...</p></div>;
  }

  return (
    <div className="admin-page-container">
      <button onClick={() => setView('main')} className="btn btn-secondary btn-sm" style={{ marginBottom: 'var(--s6)' }}>
        <ArrowLeft size={16} /> Back
      </button>
      
      <header className="admin-header">
        <div className="admin-header-content">
          <h1>Admin Panel</h1>
          <p className="admin-header-subtitle">Manage users, jobs, and system settings</p>
        </div>
        <div className="admin-header-actions">
          {activeTab === 'users' && (
            <button onClick={() => setShowCreateModal(true)} className="btn btn-primary">
              <Plus size={16} /> Create User
            </button>
          )}
          <button onClick={() => setShowResetModal(true)} className="btn btn-warning">
            <RotateCcw size={16} /> System Reset
          </button>
        </div>
      </header>

      <div className="card mb-6">
        <div className="card-header"><h2 className="section-title"><Activity size={20} />System Overview</h2></div>
        <div className="card-body">
          <div className="system-overview-grid">
            {statsCards.map((stat, i) => (
              <div key={i} className="metric-card">
                <div className="metric-icon" style={{ color: stat.color }}><stat.icon size={24} /></div>
                <div className="metric-content">
                  <div className="metric-value">{statsLoading ? '...' : stat.value}</div>
                  <h3>{stat.label}</h3>
                  {stat.subtitle && <div className="metric-subtitle">{stat.subtitle}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <nav className="admin-tabs">
        <button className={`admin-tab-button ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}><Users size={16} /> User Accounts</button>
        <button className={`admin-tab-button ${activeTab === 'jobs' ? 'active' : ''}`} onClick={() => setActiveTab('jobs')}><Activity size={16} /> Job Activity</button>
        <button className={`admin-tab-button ${activeTab === 'logs' ? 'active' : ''}`} onClick={() => setActiveTab('logs')}><FileText size={16} /> API Logs</button>
      </nav>

      {activeTab === 'users' ? (
        <div className="card">
          <div className="card-header"><h2 className="section-title"><Users size={20} /> User Accounts</h2></div>
          <div className="card-body" style={{ padding: 0 }}>
            {users.length === 0 ? (
              <div className="empty-section">
                <Users size={48} /><h4>No Users Found</h4><p>Create your first user to get started.</p>
                <button onClick={() => setShowCreateModal(true)} className="btn btn-primary"><Plus size={16} /> Create User</button>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="table-premium">
                  <thead>
                    <tr>
                      <th>User</th><th>API Key</th><th>Permissions</th><th>Status</th><th>Created</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <tr key={user.api_key}>
                        <td><span className="font-semibold">{user.name}</span></td>
                        <td>
                          <div className="api-key-cell">
                            <code className="api-key-code">{maskApiKey(user.api_key)}</code>
                            <button className="btn-icon" onClick={() => copyApiKey(user.api_key, user.name)} title="Copy API key"><Copy size={12} /></button>
                          </div>
                        </td>
                        <td>
                          <div className="permission-badge-group">
                            {user.permissions.slice(0, 2).map(p => <span key={p} className="permission-badge">{p}</span>)}
                            {user.permissions.length > 2 && <span className="permission-badge more" title={user.permissions.slice(2).join(', ')}>+{user.permissions.length - 2}</span>}
                          </div>
                        </td>
                        <td><span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>{user.is_active ? 'Active' : 'Inactive'}</span></td>
                        <td>{formatDate(user.created_at)}</td>
                        <td>
                          <div className="table-actions">
                            <button className="btn-icon" onClick={() => openEditModal(user)} title="Edit user"><Edit size={14} /></button>
                            <button className="btn-icon danger" onClick={() => handleDeleteUser(user)} title="Delete user"><Trash size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {totalPages > 1 && (
            <div className="card-footer">
              <button className="btn btn-secondary" onClick={() => handlePageChange(page - 1)} disabled={page <= 1}><ChevronLeft size={16} /> Previous</button>
              <span className="text-sm">Page {page} of {totalPages}</span>
              <button className="btn btn-secondary" onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages}>Next <ChevronRight size={16} /></button>
            </div>
          )}
        </div>
      ) : activeTab === 'jobs' ? (
        <JobsTable isAdminMode showUserColumn emptyStateTitle="No System Jobs Found" emptyStateDescription="Background jobs from all users will appear here." />
      ) : (
        <LogsViewer />
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => !submitting && setShowCreateModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2>Create New User</h2><button onClick={() => !submitting && setShowCreateModal(false)} className="modal-close"><X size={20} /></button></div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="create-name" className="form-label">User Name</label>
                <input id="create-name" type="text" value={createForm.name} onChange={e => setCreateForm(prev => ({ ...prev, name: e.target.value }))} className="form-input" disabled={submitting} />
              </div>
              <div className="form-group">
                <label htmlFor="create-email" className="form-label">Email (Optional)</label>
                <input id="create-email" type="email" value={createForm.email} onChange={e => setCreateForm(prev => ({ ...prev, email: e.target.value }))} className="form-input" disabled={submitting} placeholder="user@example.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Permissions</label>
                <div className="permissions-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px', marginTop: '8px' }}>
                  {AVAILABLE_PERMISSIONS.map(p => (
                    <label key={p} className="permission-checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)', cursor: 'pointer', transition: 'var(--transition)', backgroundColor: createForm.permissions.includes(p) ? 'var(--primary-50)' : 'var(--white)', borderColor: createForm.permissions.includes(p) ? 'var(--primary)' : 'var(--gray-300)' }}>
                      <input
                        type="checkbox"
                        checked={createForm.permissions.includes(p)}
                        onChange={() => toggleCreatePermission(p)}
                        disabled={submitting}
                        style={{ margin: 0 }}
                      />
                      <span style={{ textTransform: 'capitalize', fontSize: '14px', fontWeight: createForm.permissions.includes(p) ? '500' : '400', color: createForm.permissions.includes(p) ? 'var(--primary)' : 'var(--gray-700)' }}>{p}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowCreateModal(false)} disabled={submitting} className="btn btn-secondary">Cancel</button>
              <button onClick={handleCreateUser} disabled={submitting} className="btn btn-primary">{submitting ? <><Loader2 size={16} className="loading-spinner" /> Creating...</> : 'Create User'}</button>
            </div>
          </div>
        </div>
      )}

      <EditUserModal isOpen={showEditModal} onClose={() => setShowEditModal(false)} userToEdit={editingUser} onUserUpdated={loadUsers} />
      <SystemResetModal isOpen={showResetModal} onClose={() => setShowResetModal(false)} />
    </div>
  );
};

export default AdminPage;